import { type ReactNode, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import type { Counts } from "../api.js";
import { useMobile } from "../useMobile.js";
import styles from "./Shell.module.css";

export interface NavItem {
  label: string;
  /** Which count from /api/counts this row reports, if any. */
  countKey?: keyof Counts;
  /** Where it goes. Absent = a section not built yet; it renders but does not navigate. */
  to?: string;
  /** Sub-items indent 10px. */
  indent?: boolean;
  /** The RATE count is the only badge in the app: ink on a flash chip. */
  badge?: boolean;
}

/**
 * Nav order is fixed by design/README.md. LIBRARY's children are sub-items; everything else is
 * top level.
 *
 * Counts come from `/api/counts`. They were hardcoded until the wiring pass, which meant the rail
 * confidently reported 3,418 songs to a database holding none — the one number the dashboard
 * exists to make honest, lying on every screen.
 */
export const NAV: NavItem[] = [
  { label: "DASHBOARD", to: "/" },
  { label: "RATE", countKey: "needToRate", badge: true, to: "/rate" },
  { label: "LIBRARY", to: "/library/songs" },
  { label: "Songs", countKey: "songs", indent: true, to: "/library/songs" },
  { label: "Albums", countKey: "albums", indent: true, to: "/library/albums" },
  { label: "Artists", countKey: "artists", indent: true, to: "/library/artists" },
  { label: "Need to rate", countKey: "needToRate", indent: true, to: "/library/need-to-rate" },
  { label: "Incomplete albums", countKey: "incompleteAlbums", indent: true, to: "/library/incomplete" },
  { label: "Same song?", countKey: "needsTriage", indent: true, to: "/triage" },
  { label: "RANKINGS", to: "/rankings" },
  { label: "LISTS", to: "/lists" },
  { label: "PLAYLISTS", to: "/playlists" },
  { label: "STATISTICS", to: "/statistics" },
  { label: "SETTINGS", to: "/settings" },
];

/** A count that has not arrived yet is blank, never a zero — zero is a claim about the library. */
const show = (counts: Counts | null, key: NavItem["countKey"]) =>
  counts && key ? counts[key].toLocaleString() : null;

export function Rail({ counts = null, note }: { counts?: Counts | null; note?: string }) {
  const path = useRouterState({ select: (state) => state.location.pathname });
  /*
   * Below 900px the rail becomes a 44px header and the nav collapses behind MENU, expanding
   * INLINE and pushing the page down. Not a sheet, not a drawer, not an overlay: §13 rules those
   * out by name, and the rail is the map of the app — a map you can see the page behind is a
   * different, worse thing.
   */
  const [open, setOpen] = useState(false);

  const active = (item: NavItem) =>
    item.to === undefined
      ? false
      : item.to === "/rankings"
        ? // NOT `/` any more: that was true while `/` redirected here, and since Phase 6 it is
          // the dashboard. Leaving it would light two rows at once, and vermilion means one
          // thing — this is where you are.
          path.startsWith("/rankings") || path.startsWith("/album")
        : // LIBRARY is a section header that happens to be clickable. It never carries the active
          // mark: vermilion means "this is where you are", and two rows wearing it at once —
          // the header and the tab under it — makes the one signal ambiguous.
          item.label === "LIBRARY"
          ? false
          : path === item.to || path.startsWith(`${item.to}/`);

  return (
    <nav className={styles.rail} data-open={open ? "true" : undefined} aria-label="Main">
      <div className={styles.railHead}>
        <div className={styles.wordmark}>
          Track<span>Mark</span>
        </div>
        <button
          type="button"
          className={styles.menu}
          data-testid="mobile-menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "CLOSE" : "MENU"}
        </button>
      </div>
      {NAV.map((item) => {
        const count = show(counts, item.countKey);
        const body = (
          <>
            <span style={{ paddingLeft: item.indent ? 10 : 0 }}>{item.label}</span>
            {count !== null && (
              // The badge only means something when there is work in it.
              <span className={styles.navCount} data-badge={item.badge && count !== "0" ? "true" : undefined}>
                {count}
              </span>
            )}
          </>
        );

        return item.to ? (
          <Link
            key={item.label}
            to={item.to}
            className={styles.navItem}
            data-active={active(item) ? "true" : undefined}
            aria-current={active(item) ? "page" : undefined}
            // Following a link with the menu still open would leave it covering the screen it
            // just navigated to.
            onClick={() => setOpen(false)}
          >
            {body}
          </Link>
        ) : (
          // Sections that do not exist yet stay visible and inert rather than vanishing: the rail
          // is the map of the app, and a map with holes in it is harder to read than a greyed one.
          <button key={item.label} type="button" className={styles.navItem} disabled data-pending="true">
            {body}
          </button>
        );
      })}
      {note && <div className={styles.railNote}>{note}</div>}
    </nav>
  );
}

/**
 * The top bar's field is a way IN to search, not the search itself.
 *
 * Typing here navigates to `/search`, which owns the query, the results and the keyboard. A field
 * that filtered in place would need the two-half layout — and the ink band between them — on every
 * screen in the app, and §8's separation only works when it is the whole screen.
 */
export function TopBar({ context }: { context?: ReactNode }) {
  const navigate = useNavigate();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const mobile = useMobile();

  if (path === "/search") {
    // On mobile the context links are the rail's job, so on the one screen that also owns its own
    // field there is nothing left to put in the bar — and an empty 44px band is not chrome.
    if (mobile) return null;
    return (
      <div className={styles.topBar}>
        <div className={styles.context}>{context}</div>
      </div>
    );
  }

  return (
    <div className={styles.topBar}>
      <input
        className={styles.search}
        type="search"
        placeholder="/  search"
        aria-label="Search"
        data-testid="top-search"
        onChange={(event) => {
          const q = event.target.value;
          event.target.value = "";
          void navigate({ to: "/search", search: { q } });
        }}
      />
      <div className={styles.context}>{context}</div>
    </div>
  );
}

/**
 * A dark tray. Tables and lists sit inside one, with rows separated by a gap of the well's own
 * colour rather than by hairlines.
 */
export function Well({ children, rows = false }: { children: ReactNode; rows?: boolean }) {
  return <div className={styles.well}>{rows ? <div className={styles.wellRows}>{children}</div> : children}</div>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className={styles.eyebrow}>{children}</div>;
}
